import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["aria-label", "onClick"]
const _hoisted_2 = { class: "edge-dock-icon" }
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = {
  key: 3,
  class: "edge-dock-loading",
  "aria-hidden": "true"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "edge-dock-label" }

import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import YSF from '@neysf/qiyu-web-sdk'
import userService from '@/services/user'
import { getToken } from '@/utils/auth'
import useCommon from '@/composables/useCommon'
import { useRoute } from 'vue-router'

type BadgeValue = boolean | number | string | undefined | null
type EdgeDockItem = {
  key: string
  label: string
  icon: any
  onClick: () => void | Promise<void>
  badge?: BadgeValue
}

const collapsedWidth = 52
const expandedWidth = 170
const hoverOpenDelay = 240
const hoverCloseDelay = 300
const autoCollapseDelay = 10000


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const { t } = useI18n()
const store = useStore()
const { JumpToHref } = useCommon()
const { getUserInfo } = userService
const route = useRoute()
const unreadCount = ref(0)
const customerLoading = ref(false)
const isExpanded = ref(false)
const supportsHover = ref(false)

let hoverTimer: number | null = null
let autoCollapseTimer: number | null = null
let customerLoadingTimer: number | null = null
let mediaQueryList: MediaQueryList | null = null
let mediaQueryHandler: ((event: MediaQueryListEvent) => void) | null = null

const {
  appContext: {
    config: { globalProperties }
  }
} = getCurrentInstance()!

const lang = computed(() => store.state.user.language)

let customerInstance: any = null

const items = computed<EdgeDockItem[]>(() => {
  lang.value
  return [
    {
      key: 'customer-service',
      label: `${t('msg.calculate.xskf')}`,
      icon: require('@/assets/images/icons/right_kf.png'),
      onClick: openCustomerService,
      badge: unreadCount.value > 0 ? unreadCount.value : undefined
    },
    {
      key: 'freight-calc',
      label: `${t('msg.other.yf_ss')}`,
      icon: require('@/assets/images/icons/right_jisq.png'),
      onClick: () => JumpToHref('Calculate')
    },
    {
      key: 'bookmark',
      label: `${t('msg.calculate.scwz')}`,
      icon: require('@/assets/images/icons/right_sc.png'),
      onClick: handleBookmark
    },
    {
      key: 'back-to-top',
      label: `${t('msg.calculate.top_back')}`,
      icon: require('@/assets/images/icons/right_top.png'),
      onClick: scrollToTop
    }
  ]
})

const setBodyVars = () => {
  if (!document?.body) return
  document.body.style.setProperty('--edge-dock-expanded-width', `${expandedWidth}px`)
  document.body.style.setProperty('--edge-dock-collapsed-width', `${collapsedWidth}px`)
}

const clearBodyVars = () => {
  if (!document?.body) return
  document.body.style.removeProperty('--edge-dock-expanded-width')
  document.body.style.removeProperty('--edge-dock-collapsed-width')
}

const setExpandedState = (value: boolean) => {
  isExpanded.value = value
}

const startAutoCollapse = () => {
  if (supportsHover.value) return
  clearAutoCollapse()
  autoCollapseTimer = window.setTimeout(() => {
    setExpandedState(false)
  }, autoCollapseDelay)
}

const clearAutoCollapse = () => {
  if (autoCollapseTimer) {
    window.clearTimeout(autoCollapseTimer)
    autoCollapseTimer = null
  }
}

const scheduleHover = (expand: boolean) => {
  if (!supportsHover.value) return
  if (hoverTimer) {
    window.clearTimeout(hoverTimer)
    hoverTimer = null
  }
  hoverTimer = window.setTimeout(() => {
    setExpandedState(expand)
  }, expand ? hoverOpenDelay : hoverCloseDelay)
}

const handleMouseEnter = () => {
  scheduleHover(true)
}

const handleMouseLeave = () => {
  scheduleHover(false)
}

const handleDockClick = () => {
  if (supportsHover.value) return
  if (isExpanded.value) {
    clearAutoCollapse()
    setExpandedState(false)
    return
  }
  setExpandedState(true)
  startAutoCollapse()
}

const handleItemClick = async (item: EdgeDockItem) => {
  if (!supportsHover.value && !isExpanded.value) {
    setExpandedState(true)
  }
  try {
    await item.onClick()
  } finally {
    if (!supportsHover.value) {
      clearAutoCollapse()
      setExpandedState(false)
    }
  }
}

const isComponentIcon = (icon: any) => typeof icon === 'object' || typeof icon === 'function'
const isIconClass = (icon: any) =>
  typeof icon === 'string' && !/(\.png|\.svg|\.jpg|\.jpeg|\.gif|\.webp)$/i.test(icon) && !icon.startsWith('data:')

const isBadgeDot = (badge: BadgeValue) => badge === true
const shouldShowBadge = (badge: BadgeValue) => {
  if (badge === true) return true
  if (badge === false || badge === null || badge === undefined) return false
  if (badge === 0 || badge === '0' || badge === '') return false
  return true
}
const formatBadge = (badge: BadgeValue) =>
  typeof badge === 'number' || typeof badge === 'string' ? String(badge) : ''

const handleBookmark = () => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') !== -1
  const shortcut = isMac ? 'Command + D' : 'Ctrl + D'
  ElMessage({
    message: `请使用快捷键 ${shortcut} 将本页添加到书签`,
    type: 'info',
    duration: 3000,
    showClose: true
  })
}

const setCustomerLoading = (value: boolean) => {
  if (customerLoadingTimer) {
    window.clearTimeout(customerLoadingTimer)
    customerLoadingTimer = null
  }
  customerLoading.value = value
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const initCustomerService = async () => {
  if (customerInstance) return customerInstance
  try {
    const ysf = await YSF.init('45656fef3487f900cbe100fb04c8057c', {
      sdkTemplateId: 8128650,
      templateId: 8128650,
      sessionInvite: false,
      hidden: 1
    })

    globalProperties.$customer = ysf
    customerInstance = ysf
    ysf('onunread', (res) => {
      unreadCount.value = res.total
    })

    const token = store.state.user.token || getToken()
    if (token) {
      try {
        const cachedUser = store.state.user?.user
        const userInfo =
          cachedUser && cachedUser.user_id
            ? cachedUser
            : (await getUserInfo()).data?.result
        if (userInfo && (!cachedUser || !cachedUser.user_id)) {
          store.dispatch('user/setUserInfo', userInfo)
        }
        ysf('config', {
          uid: userInfo.user_id,
          nickname: `${userInfo.user_id}-买到手`,
          email: userInfo.email,
          mobile_phone: userInfo.mobile,
          data: JSON.stringify([
            { key: 'uid', value: userInfo.user_id, label: 'UserId' },
            { key: 'nickname', value: `${userInfo.user_id}-买到手`, label: '昵称' },
            { key: 'mobile_phone', value: userInfo.mobile, label: '手机号码' },
            { key: 'email', value: userInfo.email, label: '邮箱' }
          ]),
          language: lang.value === 'zh-CN' ? 'zh-cn' : 'zh-tw'
        })
      } catch (err) {
        console.error('获取用户信息失败', err)
      }
    } else {
      ysf('config', {
        language: lang.value === 'zh-CN' ? 'zh-cn' : 'zh-tw'
      })
    }

    return ysf
  } catch (error) {
    console.error('客服 SDK 初始化失败', error)
    throw error
  }
}

async function openCustomerService() {
  setCustomerLoading(true)
  try {
    const ysf = await initCustomerService()
    ysf('open')
    unreadCount.value = 0
  } catch (error) {
    console.error('打开客服失败', error)
  } finally {
    customerLoadingTimer = window.setTimeout(() => {
      setCustomerLoading(false)
    }, 900)
  }
}

const setupHoverMedia = () => {
  if (!window?.matchMedia) return
  mediaQueryList = window.matchMedia('(hover: hover) and (pointer: fine)')
  supportsHover.value = mediaQueryList.matches
  mediaQueryHandler = (event: MediaQueryListEvent) => {
    supportsHover.value = event.matches
    if (!supportsHover.value && isExpanded.value) {
      startAutoCollapse()
    } else {
      clearAutoCollapse()
    }
  }
  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', mediaQueryHandler)
  } else {
    mediaQueryList.addListener(mediaQueryHandler)
  }
}

const teardownHoverMedia = () => {
  if (!mediaQueryList || !mediaQueryHandler) return
  if (mediaQueryList.removeEventListener) {
    mediaQueryList.removeEventListener('change', mediaQueryHandler)
  } else {
    mediaQueryList.removeListener(mediaQueryHandler)
  }
  mediaQueryList = null
  mediaQueryHandler = null
}

onMounted(() => {
  setBodyVars()
  setupHoverMedia()
  if (route.name !== 'Category') {
    initCustomerService().catch(() => {
      // Fallback: re-init on click if the eager init fails.
    })
  }
})
watch(
  () => route.name,
  (name) => {
    if (name === 'Category') return
    initCustomerService().catch(() => {
      // Fallback: re-init on click if the eager init fails.
    })
  }
)

onBeforeUnmount(() => {
  if (hoverTimer) {
    window.clearTimeout(hoverTimer)
    hoverTimer = null
  }
  if (customerLoadingTimer) {
    window.clearTimeout(customerLoadingTimer)
    customerLoadingTimer = null
  }
  clearAutoCollapse()
  teardownHoverMedia()
  setExpandedState(false)
  clearBodyVars()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["edge-dock", { 'is-expanded': isExpanded.value }]),
    role: "toolbar",
    "aria-label": "Quick tools",
    onMouseenter: handleMouseEnter,
    onMouseleave: handleMouseLeave,
    onClick: handleDockClick
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
      return (_openBlock(), _createElementBlock("button", {
        key: item.key,
        class: "edge-dock-item",
        type: "button",
        "aria-label": item.label,
        onClick: _withModifiers(($event: any) => (handleItemClick(item)), ["stop"])
      }, [
        _createElementVNode("span", _hoisted_2, [
          (isComponentIcon(item.icon))
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), {
                key: 0,
                class: "edge-dock-icon-svg",
                "aria-hidden": "true"
              }))
            : (isIconClass(item.icon))
              ? (_openBlock(), _createElementBlock("i", {
                  key: 1,
                  class: _normalizeClass(item.icon),
                  "aria-hidden": "true"
                }, null, 2))
              : (_openBlock(), _createElementBlock("img", {
                  key: 2,
                  src: item.icon,
                  alt: item.label
                }, null, 8, _hoisted_3)),
          (item.key === 'customer-service' && customerLoading.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4))
            : _createCommentVNode("", true),
          (shouldShowBadge(item.badge))
            ? (_openBlock(), _createElementBlock("span", {
                key: 4,
                class: _normalizeClass(["edge-dock-badge", { 'is-dot': isBadgeDot(item.badge) }])
              }, [
                _renderSlot(_ctx.$slots, "badge", {
                  item: item,
                  badge: item.badge
                }, () => [
                  (!isBadgeDot(item.badge))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(formatBadge(item.badge)), 1))
                    : _createCommentVNode("", true)
                ])
              ], 2))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("span", _hoisted_6, _toDisplayString(item.label), 1)
      ], 8, _hoisted_1))
    }), 128))
  ], 34))
}
}

})